import React, { useState, useEffect } from 'react';

const Applications = () => {
  const [activeCategory, setActiveCategory] = useState('industry');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Handle screen resize to update the image dynamically
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categories = ['industry', 'consumer goods', 'food and beverages'];

  const industryImages = {
    'Aerospace Industry': 'aerospace',
    'Automobile Industry': 'automobile',
    'Bulk Powder (Chemicals, Cement, etc)': 'bulk-powder',
    'Bulk Powder, Nuts & Grains': 'bulk-powder-nuts-grains',
    'Canned Food': 'canned-food',
    'Electronics': 'electronics',
    'Export': 'exports',
    'Furniture & Handicrafts': 'furniture-handicrafts',
    'Leather Goods': 'leather-goods',
    'Metal / Tin Cans': 'metal-tin-cans',
    'Pharmaceutical': 'pharmaceutical',
    'Steel': 'steel',
    'Textile Products': 'textile',
    'Wire': 'wire-cables',
    'Dry Food': 'dry-food',
    'Glass Bottled Products': 'glass-bottles',
    'Milk Powder': 'milk-powder',
    'Milled Products': 'milled-products',
    'Pet Food': 'pet-food',
    'Plastic Bottled Products': 'plastic-bottles',
    'Spices': 'spices',
  };

  const applicationsData = [
    {
      industry: 'Automobile Industry',
      category: 'industry',
      description:
        'Desiccants are essential in the automobile industry for moisture control, preventing corrosion, and enhancing product longevity. They are used in various components, manufacturing processes, and shipping to protect sensitive automotive parts',
    },
    {
      industry: 'Aerospace Industry',
      category: 'industry',
      description:
        'Desiccants play a crucial role in the aerospace industry by protecting sensitive components from moisture-related damage, ensuring reliability in extreme environments. They are used in aircraft, spacecraft, and defense systems to prevent corrosion, electrical failures, and material degradation.',
    },
    {
      industry: 'Bulk Powder (Chemicals, Cement, etc)',
      category: 'industry',
      description:
        'Bulk powders, such as pharmaceuticals, food ingredients, chemicals, and industrial materials, are highly sensitive to moisture. Exposure to humidity can cause clumping, caking, degradation, microbial growth, and loss of quality. Desiccants help maintain low moisture levels during storage, packaging, and transportation.',
    },
    {
      industry: 'Electronics',
      category: 'industry',
      description:
        'Moisture is a major threat to electronic components, circuit boards, and semiconductor devices, leading to corrosion, short circuits, and reduced lifespan. Desiccants are used to absorb excess humidity and protect electronics during manufacturing, storage, and shipping.',
    },
    {
      industry: 'Export',
      category: 'industry',
      description:
        'In the export industry, moisture control is essential to prevent damage to goods during long-distance shipping and storage. Fluctuating temperatures and humidity levels can lead to condensation ("container rain"), mold growth, corrosion, and product degradation. Top-Dri desiccants help absorb excess moisture and protect products throughout transit.',
    },
    {
      industry: 'Metal / Tin Cans',
      category: 'industry',
      description:
        'Metal and tin cans are susceptible to corrosion, oxidation, and moisture-related damage, especially in storage and export. Desiccants help absorb excess humidity, preventing rust formation, product degradation, and condensation buildup inside sealed or unsealed containers.',
    },
    {
      industry: 'Pharmaceutical',
      category: 'industry',
      description:
        'In the pharmaceutical industry, moisture control is essential to preserve drug stability, prevent degradation, and extend shelf life. Many medications, including tablets, capsules, and diagnostic kits, are highly sensitive to humidity, which can cause clumping, reduced potency, and microbial growth. Desiccants are used in packaging, storage, and transportation to maintain product integrity.',
    },
    {
      industry: 'Steel',
      category: 'industry',
      description:
        'In the steel industry, moisture is a major concern as it leads to corrosion, oxidation, and degradation of raw materials, finished products, and machinery. Desiccants help control humidity during storage, transportation, and manufacturing, ensuring the longevity and quality of steel products.',
    },
    {
      industry: 'Wire',
      category: 'industry',
      description:
        'Moisture is a major concern in the wire and cable industry, as it can cause corrosion, oxidation, insulation degradation, and electrical failures. Desiccants are used to protect raw materials, finished wire products, and packaging from humidity-related damage.',
    },
    {
      industry: 'Textile Products',
      category: 'consumer goods',
      description:
        'In the textile industry, moisture control is crucial at various stages, from raw material storage to finished product shipping. Desiccants play a key role in preventing mold growth, fabric degradation, and machinery corrosion.',
    },
    {
      industry: 'Leather Goods',
      category: 'consumer goods',
      description:
        'Leather goods, including shoes, bags, jackets, furniture, and accessories, are highly susceptible to moisture damage, mold growth, mildew, and odors. Desiccants help absorb excess humidity during storage, packaging, and shipping, ensuring the leather remains in top condition.',
    },
    {
      industry: 'Furniture & Handicrafts',
      category: 'consumer goods',
      description:
        'Furnishings, handicrafts, and accessories made from wood, fabric, metal, ceramics, leather, or natural fibers are highly susceptible to moisture-related damage, including mold, warping, rust, and odors. Desiccants help maintain low humidity levels during storage, packaging, and transportation, ensuring product integrity.',
    },
    {
      industry: 'Bulk Powder, Nuts & Grains',
      category: 'food and beverages',
      description:
        'Moisture is a major concern in the storage and transportation of bulk powders, beans, nuts, and grains, as it can lead to clumping, mold growth, spoilage, and loss of quality. Desiccants help control humidity, ensuring that these food products remain dry, fresh, and free from microbial contamination.',
    },
    {
      industry: 'Canned Food',
      category: 'food and beverages',
      description:
        'While canned food is sealed to prevent spoilage, moisture-related issues can still arise during storage and transportation. External humidity can cause rusting of metal cans, while internal condensation can affect product quality. Desiccants help prevent corrosion, extend shelf life, and protect packaging integrity.',
    },
    {
      industry: 'Dry Food',
      category: 'food and beverages',
      description:
        'Dry food products, such as snacks, powdered foods, dried fruits, spices, grains, and pet food, are highly susceptible to moisture absorption, clumping, mold growth, and spoilage. Desiccants help maintain freshness, texture, and shelf life by preventing excess humidity inside packaging, storage, and shipping containers.',
    },
    {
      industry: 'Glass Bottled Products',
      category: 'food and beverages',
      description:
        'Glass bottled products, such as beverages, sauces, perfumes, pharmaceuticals, and cosmetics, are prone to moisture-related issues, including label damage, condensation, mold growth, and product degradation. Desiccants help maintain product integrity, packaging aesthetics, and shelf life by absorbing excess humidity during storage, packaging, and transportation.',
    },
    {
      industry: 'Plastic Bottled Products',
      category: 'food and beverages',
      description:
        'Plastic bottled products, such as beverages, pharmaceuticals, cosmetics, and chemicals, are vulnerable to moisture-related issues like label damage, condensation, microbial growth, and product degradation. Desiccants help absorb excess humidity, ensuring product quality, extended shelf life, and intact packaging during storage, transportation, and retail display.',
    },
    {
      industry: 'Milk Powder',
      category: 'food and beverages',
      description:
        'Milk products, including milk powder, dairy-based supplements, infant formula, and whey protein, are highly sensitive to moisture, clumping, spoilage, and bacterial growth. Desiccants play a crucial role in maintaining product freshness, preventing caking, and extending shelf life during storage, packaging, and transportation.',
    },
    {
      industry: 'Milled Products',
      category: 'food and beverages',
      description:
        'Milled products, such as flour, cornmeal, rice flour, semolina, starch, and other grain-based powders, are highly susceptible to moisture absorption, clumping, mold growth, and spoilage. Desiccants help maintain dryness, prevent caking, and extend shelf life during packaging, storage, and transportation.',
    },
    {
      industry: 'Pet Food',
      category: 'food and beverages',
      description:
        'Pet food, including dry kibble, treats, dehydrated meals, and supplements, is highly sensitive to moisture absorption, mold growth, spoilage, and loss of crunchiness. Desiccants play a crucial role in maintaining freshness, preventing bacterial contamination, and extending shelf life during storage, packaging, and transportation.',
    },
    {
      industry: 'Spices',
      category: 'food and beverages',
      description:
        'Spices are highly sensitive to moisture absorption, clumping, mold growth, and loss of aroma and potency. Desiccants help maintain dryness, preserve flavor, and extend shelf life during packaging, storage, and transportation.',
    },
  ];

  const filteredApplications = applicationsData.filter((app) => app.category === activeCategory);

  return (
    <section id="applications" className="w-full min-h-screen bg-white py-12 flex justify-center items-center">
      <div className="container mx-auto px-4">
        {/* Title and Subtitle */}
        <div className="text-center mb-12">
          <h2 className="text-lg font-semibold text-gray-500 uppercase tracking-wider">Desiccant Applications</h2>
          <h1 className="text-4xl font-semibold text-[#30779b] mt-3">Where to Use Our Desiccants</h1>
          <p className="text-base text-gray-600 mt-2">Discover the diverse range of applications for our desiccants</p>
        </div>

        {/* Category Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                activeCategory === category ? 'bg-[#0a354a] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Application Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredApplications.map((app, index) => {
            const imageFile = industryImages[app.industry];
            const imagePath = imageFile
              ? `./assets/imgs/headers/${imageFile}${isMobile ? '-mobile' : ''}.png`
              : null;

            return (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden transition-transform transform hover:scale-105">
                {/* Industry Image (Dynamic Based on Screen Size) */}
                {imagePath && <img src={imagePath} alt={app.industry} className="w-full h-32 object-cover" />}

                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#30779b] text-center mb-2">{app.industry}</h3>
                  <p className="text-gray-700 text-sm text-justify">{app.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Applications;
